import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { enrollmentsApi } from "@/api";
import type { EnrollmentFilter, RemovalFilter, RemoveStudentData } from "@/api";
import { QUERY_KEYS } from "@/constants";

// ─── O'quvchi ─────────────────────────────────────────────────────────────────

/** Joriy o'quvchi yozilgan kurslar. */
export const useMyEnrollments = (filter: EnrollmentFilter) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MY_ENROLLMENTS, filter],
    queryFn: () => enrollmentsApi.getMy(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });
};

/** Kursga yozilish holatim (yozilmagan bo'lsam `null`). */
export const useMyEnrollmentStatus = (courseId: number | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MY_ENROLLMENT_STATUS, courseId],
    queryFn: () => enrollmentsApi.getMyStatus(courseId as number),
    enabled: !!courseId,
    select: res => res.data,
  });
};

export const useEnroll = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: number) => enrollmentsApi.enroll(courseId),
    onSuccess: (_res, courseId) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_ENROLLMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_ENROLLMENT_STATUS, courseId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, courseId] });
      toast.success(t("ENROLLMENT.ENROLLED_TOAST"));
    },
  });
};

export const useUnenroll = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: number) => enrollmentsApi.unenroll(courseId),
    onSuccess: (_res, courseId) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_ENROLLMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_ENROLLMENT_STATUS, courseId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE, courseId] });
      toast.success(t("ENROLLMENT.UNENROLLED_TOAST"));
    },
  });
};

// ─── O'qituvchi / admin ───────────────────────────────────────────────────────

/** Kursga yozilgan o'quvchilar ro'yxati. */
export const useCourseRoster = (courseId: number | undefined, filter: EnrollmentFilter) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_ROSTER, courseId, filter],
    queryFn: () => enrollmentsApi.getRoster(courseId as number, filter),
    enabled: !!courseId,
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });
};

/** Kurs bo'yicha yozilishlar soni, holat kesimida. */
export const useEnrollmentStats = (courseId: number | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_ENROLLMENT_STATS, courseId],
    queryFn: () => enrollmentsApi.getStats(courseId as number),
    enabled: !!courseId,
    select: res => res.data,
  });
};

// ─── Chetlashtirish ───────────────────────────────────────────────────────────

/** Ro'yxat, sonlar va chetlashtirish tarixi — hammasi birga eskiradi. */
const invalidateRemovalViews = (
  queryClient: ReturnType<typeof useQueryClient>,
  courseId: number,
): void => {
  for (const key of [
    QUERY_KEYS.GET_COURSE_ROSTER,
    QUERY_KEYS.GET_ENROLLMENT_STATS,
    QUERY_KEYS.GET_COURSE_REMOVALS,
    QUERY_KEYS.GET_COURSE,
  ]) {
    void queryClient.invalidateQueries({ queryKey: [key, courseId] });
  }
  void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_REMOVALS] });
};

/** Bitta kursning chetlashtirishlar tarixi. */
export const useCourseRemovals = (courseId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_REMOVALS, courseId],
    queryFn: () => enrollmentsApi.getCourseRemovals(courseId as number),
    enabled: !!courseId,
    select: res => res.data,
  });

/** Butun platforma bo'yicha chetlashtirishlar — nazorat ro'yxati. */
export const useRemovals = (filter: RemovalFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_REMOVALS, filter],
    queryFn: () => enrollmentsApi.listRemovals(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

/**
 * Chetlashtirishlar soni: amaldagilar va qaytarilganlar.
 *
 * Yig'ma endpoint YO'Q, shuning uchun har bir holat uchun `limit: 1` bilan
 * bitta so'rov ketadi va `meta.total` o'qiladi — son haqiqiy, taxmin emas.
 */
export const useRemovalCounts = () => {
  const [open, readmitted] = useQueries({
    queries: [true, false].map(isOpen => ({
      queryKey: [QUERY_KEYS.GET_REMOVALS, "count", isOpen],
      queryFn: () => enrollmentsApi.listRemovals({ page: 1, limit: 1, open: isOpen }),
      select: (res: Awaited<ReturnType<typeof enrollmentsApi.listRemovals>>) => res.meta.total,
    })),
  });

  const counts = { open: open?.data, readmitted: readmitted?.data };
  return {
    counts: {
      ...counts,
      total:
        counts.open === undefined || counts.readmitted === undefined
          ? undefined
          : counts.open + counts.readmitted,
    },
    isLoading: !!open?.isLoading || !!readmitted?.isLoading,
    isError: !!open?.isError || !!readmitted?.isError,
  };
};

export const useRemoveStudent = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, data }: { studentId: number; data: RemoveStudentData }) =>
      enrollmentsApi.removeStudent(courseId, studentId, data),
    onSuccess: () => {
      invalidateRemovalViews(queryClient, courseId);
      toast.success(t("REMOVAL.REMOVED_TOAST"));
    },
  });
};

export const useReadmitStudent = (courseId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, note }: { studentId: number; note?: string }) =>
      enrollmentsApi.readmitStudent(courseId, studentId, note),
    onSuccess: res => {
      invalidateRemovalViews(queryClient, courseId);
      toast.success(
        res.data.restored ? t("REMOVAL.READMITTED_TOAST") : t("REMOVAL.BAN_LIFTED_TOAST"),
      );
    },
  });
};
